import { View, Input, Button } from "@/components/ui";
import { InputFormControl } from "@/components/shared/input-form-control";
import { PrimaryAccountCheckbox } from "../primary-account-checkbox";
import { FormInputPhoneNumber } from "@/components/shared/input-phone-number/controlled";
import { useFieldArray, useFormContext } from "react-hook-form";
import {
  MAX_PHONE_CONTACT_FIELDS,
  MAX_EMAIL_CONTACT_FIELDS,
} from "@/constants";
import { Typography } from "@/components/shared/typography";
import { IconButton } from "@/components/ui/icon-button";
import { useDeleteContactMutation } from "@/gqlhooks/profile/useDeleteContactMutation";
import { useParams } from "@/components/hooks/use-params";
import { useRoute } from "@/components/shared/router";
import { showToast } from "@/components/ui/toast/show-toast";
import { getErrorMessage } from "@/lib/helpers/string";

interface UpdateContactFormProps {
  onSubmit?: () => void;
  updateContactLoading?: boolean;
  showTooltip: boolean;
}

export const UpdateContactForm = ({
  onSubmit,
  updateContactLoading,
  showTooltip,
}: UpdateContactFormProps) => {
  const { id } = useParams<{ id: string }>();
  const { push } = useRoute();
  const [deleteContactMutation, { loading: deleteContactLoading }] =
    useDeleteContactMutation();
  const { control } = useFormContext();

  const onDeleteContact = async () => {
    try {
      if (!id) return;

      const result = await deleteContactMutation({
        variables: {
          input: {
            id: id,
          },
        },
      });

      if (result.data?.deleteContact?.result) {
        push({
          pageKey: "contacts",
        });
        showToast({
          title: "Success delete contact",
          type: "success",
        });
      }
    } catch (error) {
      const errMsg = getErrorMessage(error, "Error delete contact");
      showToast({
        title: errMsg,
        type: "error",
      });
    }
  };

  const {
    fields: emailFields,
    append: appendEmail,
    remove: removeEmail,
  } = useFieldArray({
    control,
    name: "email",
  });

  const {
    fields: phoneFields,
    append: appendPhone,
    remove: removePhone,
  } = useFieldArray({
    control,
    name: "phoneNumber",
  });

  const phoneFieldsDisabled = phoneFields.length >= MAX_PHONE_CONTACT_FIELDS;
  const emailFieldsDisabled = emailFields.length >= MAX_EMAIL_CONTACT_FIELDS;

  return (
    <View className="p-4 md:p-0">
      <InputFormControl
        name="firstName"
        label="First Name"
        className="mt-2"
        control={control}
        component={Input}
        componentProps={{
          placeholder: "Your First Name",
        }}
      />
      <InputFormControl
        name="lastName"
        label="Last Name"
        className="mt-2"
        control={control}
        component={Input}
        componentProps={{
          placeholder: "Your Last Name",
        }}
      />

      <View className="mt-2">
        <Typography variant="body-sm">Email</Typography>
        <View className="flex flex-col gap-1">
          {emailFields.map((field, index) => (
            <View
              key={field.id}
              className="mt-2 flex flex-row items-start gap-2"
            >
              <InputFormControl
                label=""
                className="flex-1 pb-0"
                name={`email.${index}.value`}
                control={control}
                component={Input}
                componentProps={{
                  placeholder: "example@mail.com",
                }}
              />
              {index === 0 ? (
                <IconButton
                  iconName="plus"
                  disabled={emailFieldsDisabled}
                  onClick={() => appendEmail({ value: "" })}
                />
              ) : (
                <IconButton
                  iconName="minus"
                  onClick={() => removeEmail(index)}
                />
              )}
            </View>
          ))}
        </View>
        {emailFieldsDisabled && (
          <Typography variant="body-sm" color="brand-primary" className="mt-1">
            You can add up to 3 email addresses.
          </Typography>
        )}
      </View>

      <View className="mb-3 mt-2">
        <Typography variant="body-sm">Phone Number</Typography>
        <View className="flex flex-col gap-1">
          {phoneFields.map((field, index) => (
            <View
              key={field.id}
              className="mt-2 flex flex-row items-start gap-2 md:mt-2"
            >
              <FormInputPhoneNumber
                className="mt-1 flex-1"
                label=""
                name={`phoneNumber.${index}.value`}
              />
              {index === 0 ? (
                <IconButton
                  className="mt-1"
                  iconName="plus"
                  disabled={phoneFieldsDisabled}
                  onClick={() => appendPhone({ value: "+65" })}
                />
              ) : (
                <IconButton
                  className="mt-1"
                  iconName="minus"
                  onClick={() => removePhone(index)}
                />
              )}
            </View>
          ))}
        </View>
        {phoneFieldsDisabled && (
          <Typography variant="body-sm" color="brand-primary" className="mt-1">
            You can add up to 3 phone numbers.
          </Typography>
        )}
      </View>

      <PrimaryAccountCheckbox showTooltip={showTooltip} />

      <View className="mt-2 flex flex-col justify-end gap-2 md:mt-1 md:flex-row md:items-end">
        <Button
          variant="secondary"
          color="danger"
          loading={deleteContactLoading || updateContactLoading}
          onClick={onDeleteContact}
          iconName="trash"
          iconColor="danger"
          children="Delete Contact"
        />
        <Button
          variant="primary"
          color="CTA"
          size="lg"
          loading={updateContactLoading}
          onClick={onSubmit}
          className="native:hidden"
          children="Update"
        />
      </View>
    </View>
  );
};
